import React, { useState, useEffect } from 'react';
import { BasicPage } from "../components/BasicPage";
import { useAuth, apiFetch } from "../hooks/useAuth";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, TextField, Button, Box, Grid, Typography, Chip, IconButton,
    InputAdornment, FormControl, InputLabel, Select, MenuItem, Alert,
    LinearProgress, Card, CardContent
} from "@mui/material";
import {
    Receipt as ReceiptIcon,
    Search as SearchIcon,
    CloudDownload as CloudDownloadIcon,
    FilterList as FilterIcon,
    DateRange as DateIcon
} from "@mui/icons-material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/de';

export const Rechnungsarchiv = () => {
    const { accountID } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [customerFilter, setCustomerFilter] = useState('alle');

    const numberFormat = (value) =>
        new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(value || 0);

    useEffect(() => {
        const fetchInvoices = async () => {
            if (!accountID) {
                setError("Keine Account-ID ausgewählt");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');

                const response = await apiFetch(`/app/invoices/${accountID}`);
                const data = await response.json();

                if (response.ok) {
                    setInvoices(data);
                    // Set filtered invoices immediately to avoid delay
                    setFilteredInvoices(data);
                } else {
                    setError(data.message || 'Fehler beim Laden der Rechnungen');
                }
            } catch (err) {
                setError(err.message || 'Fehler beim Laden der Rechnungen');
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
    }, [accountID]);

    // Debounced search effect
    useEffect(() => {
        if (searchTerm) {
            setSearchLoading(true);
        }
        
        const timeoutId = setTimeout(() => {
            let filtered = invoices;

            // Text search
            if (searchTerm) {
                filtered = filtered.filter(invoice =>
                    (invoice.Rechnungsnummer && String(invoice.Rechnungsnummer).toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (invoice.Titel && String(invoice.Titel).toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (invoice.Beschreibung && String(invoice.Beschreibung).toLowerCase().includes(searchTerm.toLowerCase()))
                );
            }

            // Date filter
            if (startDate) {
                filtered = filtered.filter(invoice =>
                    dayjs(invoice.Rechnungsdatum).isAfter(dayjs(startDate).subtract(1, 'day'))
                );
            }

            if (endDate) {
                filtered = filtered.filter(invoice =>
                    dayjs(invoice.Rechnungsdatum).isBefore(dayjs(endDate).add(1, 'day'))
                );
            }

            // Customer filter
            if (customerFilter !== 'alle') {
                filtered = filtered.filter(invoice => invoice.Kundennummer === customerFilter);
            }

            setFilteredInvoices(filtered);
            setSearchLoading(false);
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [invoices, searchTerm, startDate, endDate, customerFilter]);

    const handleDownload = async (invoice) => {
        try {
            // Determine the PDF name based on document type and number
            let pdfName = '';
            if (invoice.Belegart === 'Rechnung') {
                pdfName = `RG_${invoice.Rechnungsnummer}`;
            } else if (invoice.Belegart === 'Gutschrift') {
                pdfName = `GS_${invoice.Rechnungsnummer}`;
            } else {
                pdfName = `${invoice.Belegart}_${invoice.Rechnungsnummer}`;
            }

            // Use the existing download endpoint format: /app/download/{customerID}/{pdfName}
            const response = await apiFetch(`/app/download/${invoice.Kundennummer}/${pdfName}`);
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                
                // Get filename from Content-Disposition header or use default
                const contentDisposition = response.headers.get('Content-Disposition');
                const fileName = contentDisposition
                    ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                    : `${pdfName}.pdf`;
                
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                const errorText = await response.text();
                setError(errorText || 'Fehler beim Download der Rechnung');
            }
        } catch (err) {
            setError('Fehler beim Download der Rechnung');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStartDate(null);
        setEndDate(null);
        setCustomerFilter('alle');
    };

    // Get unique customer numbers for filter dropdown
    const getUniqueCustomerNumbers = () => {
        const uniqueCustomers = [...new Set(invoices.map(invoice => invoice.Kundennummer).filter(Boolean))];
        return uniqueCustomers.sort();
    };

    if (loading) {
        return (
            <BasicPage title="Rechnungsarchiv" icon={<ReceiptIcon />}>
                <Box sx={{ width: '100%', mt: 4 }}>
                    <LinearProgress />
                    <Typography variant="body1" sx={{ mt: 2, textAlign: 'center' }}>
                        Lade Rechnungen...
                    </Typography>
                </Box>
            </BasicPage>
        );
    }

    return (
        <BasicPage title="Rechnungsarchiv" icon={<ReceiptIcon />}>
            <Box sx={{ mb: 3 }}>
                <Card>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary">
                            📄 Hier finden Sie alle Ihre Rechnungen seit 2023 zum Download. 
                            Nutzen Sie die Suchfunktion, um schnell bestimmte Rechnungen zu finden.
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Search and Filter Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <FilterIcon sx={{ mr: 1 }} />
                    Suchen und Filtern
                </Typography>
                
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Volltext-Suche"
                            placeholder="Rechnungsnummer, Titel, Verlag..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Von Datum"
                                value={startDate}
                                onChange={(newValue) => setStartDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label="Bis Datum"
                                value={endDate}
                                onChange={(newValue) => setEndDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Grid>
                    </LocalizationProvider>
                    
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Kundennummer</InputLabel>
                            <Select
                                value={customerFilter}
                                label="Kundennummer"
                                onChange={(e) => setCustomerFilter(e.target.value)}
                            >
                                <MenuItem value="alle">Alle</MenuItem>
                                {getUniqueCustomerNumbers().map(customerNumber => (
                                    <MenuItem key={customerNumber} value={customerNumber}>
                                        {customerNumber}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={1}>
                        <Button
                            variant="outlined"
                            onClick={clearFilters}
                            sx={{ height: '56px' }}
                            fullWidth
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Results Summary */}
            <Box sx={{ mb: 2 }}>
                {searchLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress sx={{ flexGrow: 1, height: 4 }} />
                        <Typography variant="body2" color="text.secondary">
                            Suche...
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {invoices.length === 0 ? 
                            'Keine Rechnungen verfügbar' : 
                            `${filteredInvoices.length} von ${invoices.length} Rechnungen gefunden`
                        }
                    </Typography>
                )}
            </Box>

            {/* Invoices Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Rechnungsnummer</TableCell>
                            <TableCell>Kundennummer</TableCell>
                            <TableCell>Datum</TableCell>
                            <TableCell>Betrag</TableCell>
                            <TableCell>Beschreibung</TableCell>
                            <TableCell align="center">Aktionen</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredInvoices.length > 0 ? (
                            filteredInvoices.map((invoice, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice.Rechnungsnummer || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {invoice.Kundennummer || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {invoice.Rechnungsdatum ? 
                                            dayjs(invoice.Rechnungsdatum).format('DD.MM.YYYY') : 
                                            'N/A'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {numberFormat(invoice.Betrag)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                lineHeight: 1.4
                                            }}
                                            title={invoice.Titel || invoice.Beschreibung || 'Keine Beschreibung'}
                                        >
                                            {invoice.Titel || invoice.Beschreibung || 'Keine Beschreibung'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleDownload(invoice)}
                                            title="Rechnung herunterladen"
                                        >
                                            <CloudDownloadIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                        {invoices.length === 0 ? 
                                            'Keine Rechnungen gefunden' : 
                                            'Keine Rechnungen entsprechen den Filterkriterien'
                                        }
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </BasicPage>
    );
};
